import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@material-ui/core";
import { BktTypeaheadSelect, BktToolTip } from "front-end-lib/core";
import { BktDataTable } from "front-end-lib";
import History from "@material-ui/icons/History";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import { Validations, ILookup, IAddEditStudySite } from "../../../../model";
import { MAX_TEXT_LENGTH, PI_TEXT } from "../../../../constants";
import { getDefaultValueForSelect } from "../../../../utils/functionsHelper";
import { getPIName } from "./studySiteUtils";
import { MddCheckbox, MddValidatedTextField } from "../../../common";
import { BktSelectPersonnel, IBktDataTablePerson } from "../../../BktPersonnel";

interface IMddStudySiteGeneralCardProps {
  studySite: IAddEditStudySite;
  sites: ILookup[];
  protocolsAddedByUser: boolean;
  applyIdToAllProtocols?: boolean;
  onStudySiteChange: (studySite: IAddEditStudySite) => void;
  onSiteTypeaheadChange?: (site: ILookup) => void;
  onApplyIdToAllProtocolsChange?: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSponsorSiteIdValidationError: (hasError: boolean) => void;
  piHistoryData?: Array<{
    name: string;
    startDate: string;
    endDate: string;
    key: string;
  }> | null;
}

export const MddStudySiteGeneralCard = ({
  studySite,
  sites,
  protocolsAddedByUser,
  applyIdToAllProtocols = false,
  onStudySiteChange,
  onSiteTypeaheadChange,
  onApplyIdToAllProtocolsChange,
  onSponsorSiteIdValidationError,
  piHistoryData
}: IMddStudySiteGeneralCardProps): JSX.Element => {
  const [displayPIDialog, setDisplayPIDialog] = useState(false);

  // Determine edit mode by checking if StudySiteId exists (indicates existing study site)
  const isEditMode = Boolean(studySite.studySiteId);

  // Render PI history table
  const renderPiHistory = useCallback(() => {
    if (!piHistoryData?.length) return null;

    return (
      <BktDataTable
        data={piHistoryData}
        columnProps={[
          {
            label: "Principal Investigator",
            tableCellProps: {
              id: "piHistoryNameHeader"
            },
            divProps: { id: "piHistoryNameHeader_div" }
          },
          {
            label: "Start",
            tableCellProps: { id: "piHistoryStartHeader" },
            divProps: { id: "piHistoryStartHeader_div" }
          },
          {
            label: "End",
            tableCellProps: {
              id: "piHistoryEndHeader"
            },
            divProps: { id: "piHistoryEndHeader_div" }
          }
        ]}
        containerProps={{ id: "tableWrapper" }}
        tableProps={{
          id: "tableContainer",
          className: "mdd-study-site-tbl-history"
        }}
        tableHeadProps={{
          id: "tableHeader",
          rowProps: {
            id: "tableHeaderRow",
            className: "mdd-study-site-tbl-history--header-row"
          }
        }}
        tableBodyProps={{
          id: "tableBody",
          rowProps: {
            id: "tableBodyRow",
            className: "mdd-study-site-tbl-history--body-row"
          },
          cellProps: {
            className: "mdd-study-site-tbl-history--body-cell"
          }
        }}
      />
    );
  }, [piHistoryData]);

  const handleSiteTypeaheadChange = useCallback(
    (selectedOption: ILookup) => {
      const { label, value } = selectedOption || {
        value: undefined,
        label: undefined
      };
      onStudySiteChange({
        ...studySite,
        siteId: value,
        siteName: label
      });
      onSiteTypeaheadChange?.(selectedOption);
    },
    [studySite, onStudySiteChange, onSiteTypeaheadChange]
  );

  const handleSponsorSiteIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onStudySiteChange({
        ...studySite,
        sponsorSiteId: event.target.value
      });
    },
    [studySite, onStudySiteChange]
  );

  const handleActiveCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isActive = e.target.checked;
      const protocols = studySite.protocols || [];

      let updatedStudySite: IAddEditStudySite = {
        ...studySite,
        isActive
      };

      if (!isActive && protocols.length > 0) {
        // Force existing protocols inactive, but keep newly added protocols active.  This will cause
        // an error on save as per the AC
        updatedStudySite = {
          ...updatedStudySite,
          protocols: protocols.map(protocol => ({
            ...protocol,
            isActive: !protocol.isExisting ? protocol.isActive : false
          }))
        };
      }

      onStudySiteChange(updatedStudySite);
    },
    [studySite, onStudySiteChange]
  );

  const piName = useMemo(
    () =>
      getPIName(
        studySite.primaryInvestigator
          ? {
              firstName: (studySite.primaryInvestigator as any).firstName,
              lastName: (studySite.primaryInvestigator as any).lastName
            }
          : undefined
      ),
    [studySite.primaryInvestigator]
  );

  const isApplyIdDisabled = useMemo(
    () => !protocolsAddedByUser || (studySite.protocols?.length || 0) === 0,
    [protocolsAddedByUser, studySite.protocols?.length]
  );

  const handleDisplayPIDialog = useCallback(() => {
    setDisplayPIDialog(true);
  }, []);

  const handleClosePIDialog = useCallback(() => {
    setDisplayPIDialog(false);
  }, []);

  const handlePISelect = (personnel?: IBktDataTablePerson) => {
    setDisplayPIDialog(false);
    onStudySiteChange({
      ...studySite,
      primaryInvestigator: personnel
    });
  };

  return (
    <div className="mdd-study-site--general-section-card">
      <div className="mdd-study-site--general-section-header">
        <div className="mdd-study-site--general-section-title">General</div>
        <div className="mdd-study-site--general-section-active">
          <MddCheckbox
            lblText="Active"
            chkBoxProps={{
              id: "chkStudySiteActive",
              checked: studySite.isActive,
              value: "chkStudySiteActive",
              disabled: !isEditMode, // Disabled for new sites, enabled for edit mode
              onChange: handleActiveCheckboxChange
            }}
          />
        </div>
      </div>

      <div className="mdd-study-site--general-section-content">
        <div className="mdd-study-site--general-section-left">
          <div className="mdd-study-site-div--label" id="lblSite">
            Site {!isEditMode ? "*" : ""}
          </div>
          {isEditMode ? (
            <div className="mdd-study-site-div--text" id="txtSiteName">
              <span data-testid="txtSiteName">
                {studySite.siteName || "N/A"}
              </span>
            </div>
          ) : (
            <BktTypeaheadSelect
              selectData={sites}
              textProps={{
                validationForOnBlur: [Validations.required],
                textFieldProps: {
                  textProps: {
                    id: "siteDropdown",
                    placeholder: "Select a site",
                    className: "tbl--filter-select",
                    InputLabelProps: {
                      shrink: studySite.siteId !== undefined
                    },
                    disabled: sites.length === 0,
                    value: getDefaultValueForSelect(sites, studySite.siteId)
                  }
                }
              }}
              itemProps={{ name: "siteId" }}
              onSubmit={handleSiteTypeaheadChange}
            />
          )}
        </div>

        <div className="mdd-study-site--general-section-middle">
          {isEditMode ? (
            <>
              <div
                className="mdd-study-site-div--sponsor-label"
                id="lblSponsorSiteId"
              >
                <span>
                  Sponsor Site ID
                  <span
                    className="mdd-validated-text-field--info-icon"
                    title="Sponsor Site ID cannot be edited after adding the study site"
                  >
                    <InfoIcon
                      fontSize="small"
                      id="sponsorSiteIdInfoIcon"
                      data-testid="sponsorSiteIdInfoIcon"
                    />
                  </span>
                </span>
              </div>
              <div className="mdd-study-site-div--text" id="txtSponsorSiteId">
                <span data-testid="txtSponsorSiteId">
                  {studySite.sponsorSiteId || "N/A"}
                </span>
              </div>
            </>
          ) : (
            <>
              <MddValidatedTextField
                id="sponsorSiteId"
                data-testid="sponsorSiteId"
                labelProps={{
                  labelText: "Sponsor Site ID *",
                  className: "mdd-study-site-div--sponsor-label",
                  showInfoIcon: true,
                  infoIconTooltip:
                    "Sponsor Site ID cannot be edited after adding the study site",
                  infoIconId: "sponsorSiteIdInfoIcon",
                  infoIconTestId: "sponsorSiteIdInfoIcon"
                }}
                textFieldProps={{
                  debounceInterval: 0,
                  textProps: {
                    className: "mdd-personnel-div--editable-value",
                    placeholder: "Enter ID",
                    inputProps: {
                      id: "sponsorSiteIdTextField",
                      maxLength: MAX_TEXT_LENGTH.SPONSOR_SITE_ID
                    },
                    variant: "outlined",
                    required: true,
                    type: "text",
                    onChange: handleSponsorSiteIdChange,
                    value: studySite.sponsorSiteId || ""
                  }
                }}
                validationForOnBlur={[
                  Validations.required,
                  Validations.sponsorSiteAllowedCharacters
                ]}
                validationForOnChange={[
                  Validations.sponsorSiteAllowedCharacters
                ]}
                validationerror={onSponsorSiteIdValidationError}
              />
              {onApplyIdToAllProtocolsChange && (
                <div>
                  <MddCheckbox
                    lblText="Apply ID to all protocols"
                    chkBoxProps={{
                      id: "chkApplyIdToAllProtocols",
                      checked: applyIdToAllProtocols,
                      value: "chkApplyIdToAllProtocols",
                      disabled: isApplyIdDisabled,
                      onChange: onApplyIdToAllProtocolsChange
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="mdd-study-site--general-section-right">
          <div className="mdd-study-site-div--label--with-padding" id="lblPI">
            {`${PI_TEXT} *`}
            {piHistoryData?.length && (
              <BktToolTip
                containerProps={{
                  style: {
                    visibility: "visible"
                  }
                }}
                tooltip={renderPiHistory() || <div>No history available</div>}
                position="bottom-end"
              >
                <History color={"action"} />
              </BktToolTip>
            )}
          </div>
          <div className="mdd-study-site-div--text" id="txtSitePIName">
            <span data-testid="txtSitePIName">{piName || "N/A"}</span>
          </div>
          <span className="mdd-study-site--search-button--padding">
            <Button
              className="mdd-study-site--search-button"
              size="small"
              id="btnDisplayPIDialog"
              disableFocusRipple
              disableRipple
              variant="outlined"
              onClick={handleDisplayPIDialog}
            >
              Search
            </Button>
          </span>
        </div>
      </div>

      {/* Primary Investigator Selection Dialog */}
      <BktSelectPersonnel
        displayDialog={displayPIDialog}
        person={studySite.primaryInvestigator}
        onCancel={handleClosePIDialog}
        onSelect={handlePISelect}
      />
    </div>
  );
};
